import React from "react";

// Single source of truth for payment-status visuals across the admin UI.
// Colors map to the user's spec:
//   green = Active / Paid     blue = Trial / Pending     orange = Past Due
//   gray = Cancelled          red = Expired / Unpaid
export const PAYMENT_STATUS_META = {
    active: {
        label: "Active",
        className: "bg-green-500/15 text-green-700 border-green-500/40 dark:text-green-300",
        dot: "bg-green-500",
        adminNote: "Subscription is paid and current. Plan benefits are live.",
    },
    pending: {
        label: "Pending",
        className: "bg-blue-500/15 text-blue-700 border-blue-500/40 dark:text-blue-300",
        dot: "bg-blue-500",
        adminNote: "Checkout started but Stripe hasn't confirmed payment yet. Will flip to Active on webhook.",
    },
    past_due: {
        label: "Past Due",
        className: "bg-orange-500/15 text-orange-700 border-orange-500/40 dark:text-orange-300",
        dot: "bg-orange-500",
        adminNote: "Renewal payment failed. Plan is still live but Stripe will retry; falls to downgrade policy if it keeps failing.",
    },
    cancelled: {
        label: "Cancelled",
        className: "bg-muted text-muted-foreground border-border",
        dot: "bg-gray-500",
        adminNote: "Subscription was cancelled. Public profile drops to Basic per the downgrade policy.",
    },
    expired: {
        label: "Expired",
        className: "bg-destructive/15 text-destructive border-destructive/40",
        dot: "bg-red-500",
        adminNote: "Plan expired (no renewal). Public profile drops to Basic per the downgrade policy.",
    },
    unpaid: {
        label: "Unpaid",
        className: "bg-destructive/10 text-destructive/90 border-destructive/30",
        dot: "bg-red-500",
        adminNote: "Account created but no placement ever purchased. Not generating revenue.",
    },
};

export const PLAN_META = {
    top_spot: { label: "Elite", className: "bg-amber-500/15 text-amber-800 border-amber-500/40 dark:text-amber-300" },
    featured: { label: "Featured", className: "bg-primary/15 text-primary border-primary/40" },
    basic: { label: "Basic", className: "bg-muted text-foreground/80 border-border" },
};

export function PaymentBadge({ status, small = false, testid }) {
    const meta = PAYMENT_STATUS_META[status] || PAYMENT_STATUS_META.unpaid;
    const sz = small ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5";
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${sz} ${meta.className}`}
            data-testid={testid || `payment-badge-${status}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
        </span>
    );
}

export function PlanBadge({ plan, small = false, testid }) {
    if (!plan) return <span className="text-xs text-muted-foreground">—</span>;
    const meta = PLAN_META[plan] || { label: plan, className: "bg-muted text-foreground/80 border-border" };
    const sz = small ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5";
    return (
        <span
            className={`inline-flex items-center rounded-full border font-semibold ${sz} ${meta.className}`}
            data-testid={testid || `plan-badge-${plan}`}>
            {meta.label}
        </span>
    );
}

// Render an ISO date string as "Feb 14, 2026". Returns null for falsy input
// so callers can decide their own "—" placeholder.
export function formatDate(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
        return iso.slice(0, 10);
    }
}

export function formatMoney(n) {
    const v = Number(n || 0);
    return v.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}
